import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check } from '../../icons';
import {
  listDuties, setDuties, DUTIES, DUTY_LABEL,
  type DutyPersonDto, type Duty, type DutyScope,
} from '../../api/hr-duty';
import './AttendanceAdmin.css';
import './DutyPage.css';

/**
 * 담당자 관리 — **업무를 사람에게 따로 준다** (근태 C1, 2026-08-24).
 *
 * 설계: 화면 R(담당자 관리) · K(권한 현황).
 *
 * ★★ 근태 권한이 **역할 둘로만** 갈렸다. 인사 담당에게 전사 근태를 보여 주려면 ADMIN 을 하나
 *    더 줘야 했고, 그러면 회사 설정까지 다 만질 수 있었다.
 * ★★ **부여는 이 화면 한 곳에서만** 한다. 콘솔(8444)의 「권한 현황」은 보기만 한다 —
 *    부여를 두 곳에 두면 어느 쪽이 진짜인지 알 수 없게 된다.
 * ★ **자동으로 오는 권한은 여기서 못 바꾼다** — 관리자 역할과 팀장 지정에서 온다. 회색으로
 *   보여 주되 손댈 수 없게 두고, 어디서 바꾸는지를 적는다.
 * ★ 회수는 지우는 것이 아니라 **끄는 것**이다(서버가 `revokedAt` 으로 남긴다).
 */

const ERRORS: Record<string, string> = {
  FORBIDDEN: '담당자 관리는 회사 관리자만 할 수 있습니다.',
  NOT_FOUND: '그 사용자를 찾을 수 없습니다.',
};

const SCOPE_LABEL: Record<string, string> = { ALL: '전사', TEAM: '내 팀' };

export function DutyPage(): JSX.Element {
  const [items, setItems] = useState<DutyPersonDto[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /**
   * 고른 사람 (2026-08-30 · 시안 맞춤) — 시안은 **한 사람의 카드**를 본다.
   * ★ 종전 넓은 표는 사람이 열이면 괜찮지만 백이면 못 쓴다. 그리고 묻는 것이 늘
   *   「**이 사람에게** 무엇을 줬나」라 표가 아니라 카드가 맞다.
   */
  const [picked, setPicked] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listDuties());
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '담당자 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const apply = async (p: DutyPersonDto, next: Array<{ duty: Duty; scope?: DutyScope }>, note: string) => {
    setBusy(p.userId); setError(null); setDone(null);
    try {
      await setDuties(p.userId, next);
      setDone(note);
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '저장하지 못했습니다.');
    } finally { setBusy(null); }
  };

  const current = (p: DutyPersonDto): Array<{ duty: Duty; scope?: DutyScope }> =>
    p.duties
      .filter((d) => (DUTIES as readonly string[]).includes(d.duty))
      .map((d) => ({ duty: d.duty as Duty, scope: (d.scope === 'TEAM' ? 'TEAM' : 'ALL') as DutyScope }));

  const toggle = (p: DutyPersonDto, duty: Duty, on: boolean) => {
    const next = on
      ? [...current(p).filter((d) => d.duty !== duty), { duty, scope: 'ALL' as DutyScope }]
      : current(p).filter((d) => d.duty !== duty);
    void apply(p, next, on ? `${p.name} 님에게 "${DUTY_LABEL[duty]}" 를 줬습니다.` : `${p.name} 님의 "${DUTY_LABEL[duty]}" 를 회수했습니다.`);
  };

  const setScope = (p: DutyPersonDto, duty: Duty, scope: DutyScope) => {
    void apply(
      p, current(p).map((d) => (d.duty === duty ? { ...d, scope } : d)),
      `${p.name} 님의 "${DUTY_LABEL[duty]}" 범위를 ${SCOPE_LABEL[scope]}로 바꿨습니다.`,
    );
  };

  const shown = q.trim()
    ? items.filter((p) => p.name.includes(q.trim()) || (p.position ?? '').includes(q.trim()))
    : items;
  const granted = items.filter((p) => p.duties.length > 0);
  const auto = items.filter((p) => p.duties.length === 0 && (p.isAdmin || p.leaderTeamName));
  const grantedCount = granted.length;
  const autoCount = auto.length;
  const adminCount = items.filter((p) => p.isAdmin).length;
  const leaderCount = items.filter((p) => !p.isAdmin && p.leaderTeamName).length;
  /**
   * «그 밖의 사람» — 찾을 때, **그리고 아직 아무에게도 안 줬을 때** 뜬다.
   * ★★ 운영은 지금 부여가 **0건**이다. 「찾을 때만」으로 두면 그 회사에서는 목록이 통째로 비어
   *    **줄 사람을 고를 수조차 없다** — 처음 쓰는 회사가 못 쓰는 화면이 된다.
   */
  const others = (q.trim() || granted.length === 0)
    ? shown.filter((p) => p.duties.length === 0 && !p.isAdmin && !p.leaderTeamName)
    : [];
  const person = items.find((p) => p.userId === picked)
    ?? (q.trim() ? shown[0] : granted[0] ?? auto[0] ?? others[0])
    ?? null;

  return (
    <div className="hra dutyp">
      <header className="hra__head">
        <div>
          <h1 className="hra__title">담당자 부여</h1>
          <p className="hra__sub">
            부여받은 사람 <b>{grantedCount}명</b> · 자동으로 오는 사람 <b>{autoCount}명</b>
            {(adminCount > 0 || leaderCount > 0) && (
              <>(관리자 {adminCount} · 팀장 {leaderCount})</>
            )}
          </p>
        </div>
      </header>

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}
      {loading && items.length === 0 && <p className="hra__empty">불러오는 중…</p>}

      {/*
        ★★ 시안 맞춤 (2026-08-30) — **사람을 고르고 그 사람의 카드를 본다.**
           종전은 사람 × 업무 넓은 표였다. 사람이 열이면 괜찮지만 백이면 못 쓰고,
           묻는 것이 늘 「**이 사람에게** 무엇을 줬나」라 표가 아니라 카드가 맞다.
      */}
      <div className="dutyp__split">
        <nav className="dutyp__list" aria-label="사람">
          <label className="dutyp__search">
            <span className="dutyp__sr">이름 · 부서로 찾기</span>
            <input
              type="search" value={q} placeholder="이름 · 부서로 찾기"
              aria-label="이름 · 부서로 찾기"
              onChange={(e) => setQ(e.target.value)}
            />
          </label>

          <p className="dutyp__glabel">부여받은 사람 {granted.length}</p>
          {granted.length === 0 && (
            <p className="dutyp__none">
              아직 아무에게도 주지 않았습니다 — <b>지금과 똑같이 동작합니다.</b>
            </p>
          )}
          {granted.map((p) => (
            <button
              key={p.userId} type="button"
              className={`dutyp__i${person?.userId === p.userId ? ' is-on' : ''}`}
              aria-current={person?.userId === p.userId}
              onClick={() => setPicked(p.userId)}
            >
              <span className="dutyp__nm">{p.name}</span>
              <span className="dutyp__ht">
                {p.position ?? '—'} · {p.duties.map((d) => DUTY_LABEL[d.duty as Duty] ?? d.duty).join(' · ')}
              </span>
              <span className="dutyp__n">{p.duties.length}</span>
            </button>
          ))}

          {auto.length > 0 && (
            <>
              {/* ★ 자동으로 오는 사람은 **못 끈다** — 목록에 두되 고를 수 없게 두면 「왜 안 눌리지」가 된다. */}
              <p className="dutyp__glabel">자동으로 오는 사람 <span className="dutyp__dim">· 못 끕니다</span></p>
              {auto.map((p) => (
                <button
                  key={p.userId} type="button"
                  className={`dutyp__i${person?.userId === p.userId ? ' is-on' : ''}`}
                  onClick={() => setPicked(p.userId)}
                >
                  <span className="dutyp__nm">{p.name}</span>
                  <span className="dutyp__ht">{p.isAdmin ? '관리자 역할' : `${p.leaderTeamName} 팀장`}</span>
                  <span className="dutyp__bd">{p.isAdmin ? '전부' : '팀'}</span>
                </button>
              ))}
            </>
          )}

          {others.length > 0 && (
            <>
              <p className="dutyp__glabel">그 밖의 사람 {others.length}</p>
              {others.map((p) => (
                <button
                  key={p.userId} type="button"
                  className={`dutyp__i${person?.userId === p.userId ? ' is-on' : ''}`}
                  onClick={() => setPicked(p.userId)}
                >
                  <span className="dutyp__nm">{p.name}</span>
                  <span className="dutyp__ht">{p.position ?? '—'}</span>
                </button>
              ))}
            </>
          )}
          {!q.trim() && granted.length > 0 && (
            <p className="dutyp__none">
              새로 줄 사람은 <b>위에서 찾으세요</b> — 이름을 넣으면 목록에 뜹니다.
            </p>
          )}
        </nav>

        <section className="dutyp__pane" aria-label="담당 업무">
          {!person ? (
            <p className="hra__empty">왼쪽에서 사람을 고르세요.</p>
          ) : (
            <>
              <div className="dutyp__who">
                <div>
                  <b>{person.name}</b>
                  <span className="hra__dim"> {person.position ?? '—'}</span>
                  <div className="hra__dim">
                    역할 <b>{person.isAdmin ? '회사 관리자' : '직원'}</b>
                    {person.leaderTeamName ? ` · ${person.leaderTeamName} 팀장` : ''}
                    {' · '}부여 {person.duties.length}건
                  </div>
                </div>
                <span className="dutyp__sp" />
                {/* ★ 「전부 회수」는 되돌릴 수 있는 일이지만 한 번에 여럿을 뺀다 — 물어보고 한다. */}
                {person.duties.length > 0 && !person.isAdmin && (
                  <button
                    type="button" className="hra__btn"
                    disabled={busy !== null}
                    onClick={() => {
                      if (!window.confirm(`${person.name} 님의 부여 ${person.duties.length}건을 전부 회수합니다. 계속할까요?`)) return;
                      void apply(person, [], `${person.name} 님의 담당 업무를 전부 회수했습니다.`);
                    }}
                  >전부 회수</button>
                )}
              </div>

              {person.isAdmin && (
                <p className="hra__note">
                  이 사람은 <b>회사 관리자</b>라 아래 업무를 <b>전부</b> 갖습니다 — 여기서 주고 뺄 것이 없습니다.
                  바꾸려면 <b>사용자 관리에서 역할을</b> 바꿔야 합니다.
                </p>
              )}

              <section className="dutyp__card">
                <h2 className="dutyp__cardt">
                  근태 <span className="hra__dim">근태 메뉴가 열립니다</span>
                  <span className="dutyp__sp" />
                  <span className="dutyp__bd">{person.duties.length} / {DUTIES.length}</span>
                </h2>
                <table className="hra__table dutyp__tbl">
                  <tbody>
                    {DUTIES.map((d) => {
                      const g = person.duties.find((x) => x.duty === d);
                      // 팀장이 자동으로 갖는 둘은 체크가 없어도 이미 열려 있다 — 그렇게 적는다.
                      const autoOpen = !g && !!person.leaderTeamName
                        && (d === 'ATTENDANCE_VIEW' || d === 'ATTENDANCE_APPROVE');
                      return (
                        <tr key={d} className={g ? 'is-on' : undefined}>
                          <td className="dutyp__chk">
                            <input
                              type="checkbox"
                              aria-label={`${person.name} ${DUTY_LABEL[d]}`}
                              checked={!!g || person.isAdmin}
                              disabled={person.isAdmin || busy !== null}
                              onChange={(e) => toggle(person, d, e.target.checked)}
                            />
                          </td>
                          <td><b>{DUTY_LABEL[d]}</b></td>
                          <td className="hra__dim dutyp__code">{d}</td>
                          <td>
                            {person.isAdmin ? <span className="hra__dim">전사</span>
                              : g ? (
                                <select
                                  aria-label={`${person.name} ${DUTY_LABEL[d]} 범위`}
                                  value={g.scope === 'TEAM' ? 'TEAM' : 'ALL'}
                                  disabled={busy !== null}
                                  onChange={(e) => setScope(person, d, e.target.value as DutyScope)}
                                >
                                  <option value="ALL">전사</option>
                                  <option value="TEAM">내 팀</option>
                                </select>
                              ) : autoOpen ? <span className="hra__dim">팀 자동</span>
                                : <span className="hra__dim">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>

              {/*
                ★★★ **없는 것을 그리지 않는다.** 시안에는 「인사」·「전자결재」 카드가 함께 있지만
                   서버에 그 업무가 **없다**(담당 업무는 근태 다섯뿐). 빈 카드를 세우면 「줬는데
                   안 된다」가 되고, 그건 없는 것보다 나쁘다 — 대신 **없다고 적는다.**
              */}
              <p className="hra__note">
                시안의 <b>인사</b>·<b>전자결재</b> 담당 업무는 아직 <b>서버에 없습니다</b> —
                지금 인사 화면은 <b>회사 관리자</b>만 봅니다. 업무를 만들려면 그 업무를 <b>지키는 자리</b>도
                함께 있어야 합니다(아무도 안 보는 권한은 없는 것보다 나쁩니다).
              </p>

              <p className="hra__note">
                판정 순서 — <b>본인 기록 → 회사 관리자 → 여기서 준 업무 → 팀장 자동 → 거부</b>.
                <b> 자동</b>은 관리자 역할과 팀장 지정에서 오고 여기서 못 바꿉니다 —
                <b>사용자 관리에서 역할을</b>, <b>조직 · 책임자에서 팀장을</b> 바꿔야 합니다.
                회수해도 <b>기록은 남습니다</b>.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
